import { memo, useRef, useCallback, useEffect } from "react";
import { X, Instagram, Users, Download } from "lucide-react";
import { toPng } from "html-to-image";
import { Button } from "./Button";
import { PredictionCard } from "./PredictionCard";
import { trackImageShared, trackImageGenerated } from "../lib/analytics";
import { useLanguage } from "../contexts/LanguageContext";
import { useToast } from "../contexts/ToastContext";
import type { Prediction } from "../types";

interface ShareModalProps {
  onClose: () => void;
  prediction: Prediction;
}

export const ShareModal = memo(function ShareModal({
  onClose,
  prediction,
}: ShareModalProps) {
  const { t } = useLanguage();
  const cardRef = useRef<HTMLDivElement>(null);
  const { showToast } = useToast();

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const handleDownload = useCallback(async () => {
    if (!cardRef.current) {
      showToast(t("common.downloadFailed"), "error");
      return;
    }

    try {
      // Wait for layout to settle and ensure content is rendered
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Double-check ref is still available after async operation
      if (!cardRef.current) {
        showToast(t("common.downloadFailed"), "error");
        return;
      }

      // Find the actual PredictionCard element inside the wrapper
      const cardElement =
        cardRef.current.querySelector<HTMLElement>(".gradient-primary");

      if (!cardElement) {
        showToast(t("common.downloadFailed"), "error");
        return;
      }

      // Get the exact bounding box of the card element
      const rect = cardElement.getBoundingClientRect();

      // Validate dimensions
      if (rect.width === 0 || rect.height === 0) {
        showToast(t("common.downloadFailed"), "error");
        return;
      }

      // Store original border radius and remove it for Instagram compatibility
      const originalBorderRadius = cardElement.style.borderRadius;
      const originalClassName = cardElement.className;

      // Remove rounded classes and force border-radius to 0
      cardElement.className = originalClassName.replace(/rounded-\w+/g, "");
      cardElement.style.borderRadius = "0px";
      cardElement.style.setProperty("border-radius", "0px", "important");

      // Wait a moment for style changes to apply
      await new Promise((resolve) => setTimeout(resolve, 50));

      try {
        // Generate image with high quality settings and exact dimensions
        // Capture the card element directly to avoid any wrapper spacing issues
        // Using pixelRatio 3 for better quality (3x resolution)
        const dataUrl = await toPng(cardElement, {
          quality: 1.0,
          pixelRatio: 3,
          backgroundColor: undefined,
          cacheBust: true,
          width: Math.round(rect.width),
          height: Math.round(rect.height),
        });

        // Restore original border radius
        cardElement.style.borderRadius = originalBorderRadius;

        // Sanitize filename to avoid invalid characters
        const sanitizeFilename = (str: string): string =>
          str.replace(/[^a-z0-9]/gi, "-").toLowerCase();

        const filename = `prediction-${sanitizeFilename(
          prediction.homeTeam
        )}-${sanitizeFilename(prediction.awayTeam)}.png`;

        // Create and trigger download
        const link = document.createElement("a");
        link.download = filename;
        link.href = dataUrl;
        document.body.appendChild(link);
        link.click();

        // Clean up after a short delay to ensure download starts
        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);

        // Track analytics
        trackImageGenerated("prediction");
        trackImageShared("prediction");

        showToast(t("common.downloadSuccess"), "success");
      } finally {
        // Always restore border radius and class even if there's an error
        cardElement.className = originalClassName;
        cardElement.style.borderRadius = originalBorderRadius;
        cardElement.style.removeProperty("border-radius");
      }
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.error("Download failed:", error);
      }
      showToast(t("common.downloadFailed"), "error");
    }
  }, [prediction, t, showToast]);

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-slideUp shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-primary to-primary-dark p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-2xl font-bold">{t("share.title")}</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-smooth"
              aria-label={t("common.close") || "Close"}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-white/90 text-sm">
            {t("share.subtitle") || t("share.title")}
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* Friend Competition Message */}
          <div className="bg-gradient-to-br from-primary-lighter to-primary-light rounded-xl p-4 border border-primary/20">
            <div className="flex items-start gap-3">
              <div className="bg-primary/20 p-2 rounded-lg">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-slate-900 mb-1">
                  {t("share.competitionTitle")}
                </h3>
                <p className="text-xs text-slate-700 leading-relaxed">
                  {t("share.competitionMessage")}
                </p>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="w-full flex justify-center">
            <div
              ref={cardRef}
              className="w-full"
              style={{
                margin: 0,
                padding: 0,
                lineHeight: 0,
              }}
            >
              <PredictionCard {...prediction} />
            </div>
          </div>

          {/* Instagram Sharing Instructions */}
          <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-4 border border-pink-200">
            <div className="flex items-start gap-3">
              <div className="bg-gradient-to-br from-pink-500 to-purple-600 p-2 rounded-lg">
                <Instagram className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-slate-900 mb-1">
                  {t("share.instagramTitle")}
                </h3>
                <p className="text-xs text-slate-700 leading-relaxed">
                  {t("share.instagramInstructions")}
                </p>
              </div>
            </div>
          </div>

          {/* Download Button */}
          <Button
            variant="primary"
            fullWidth
            onClick={handleDownload}
            className="!py-4"
          >
            <div className="flex items-center justify-center gap-2">
              <Download className="w-5 h-5" />
              <span className="font-semibold">{t("share.download")}</span>
            </div>
          </Button>

          {/* Secondary CTA */}
          <p className="text-center text-xs text-slate-500">
            {t("share.afterDownload")}
          </p>
        </div>
      </div>
    </div>
  );
});
